import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useContext,
  useEffect
} from 'react'
import * as Colors from '@ant-design/colors'

import { InsetSection, InsetSectionProps, StyledInsetSection } from './Styled'
import { Context } from './SectionContext'

/**
 * Context
 *
 * We want a nice and maintainable way to have a keyboard shortcut that skips the users between
 *  sections on a form, without literring all our forms with code that eventually nobody will understand.
 * This module provide an abstraction which transparently registers sections to a provider and serves
 *  global keyboard shortcuts for jumping.
 *
 * To permit extra hole punching, for instance to jump to a newly added section automatically, we also
 *  consume incoming refs and will use those if provided. Sections expose imperative handlers to assist with this.
 *
 * Very nice!
 */

export interface ISectionRefMethods {
  focus: () => void
  contains: (element: HTMLElement) => boolean
  getElement: () => HTMLElement | null
}

export type SectionProps = Omit<InsetSectionProps, '$colour' | 'children'> & {
  id?: string
  onKeyDown: (event: any) => void
  children: (props: {
    firstInputRef: React.Ref<HTMLElement>
  }) => React.ReactNode
  colour: 'primary' | 'alt'
  createdAt?: string
}

function SectionInner(
  { children, colour = 'primary', ...props }: SectionProps,
  ref: React.ForwardedRef<ISectionRefMethods>
) {
  ref = useUnstableRef(ref)

  const registerSection = useContext(Context)
  useEffect(() => {
    return registerSection(ref)
  }, [ref, registerSection])

  const containerRef = useRef<HTMLDivElement>(null)
  const firstInputRef = useRef<HTMLElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      firstInputRef.current?.focus({ preventScroll: true })

      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    },

    contains: element => {
      return containerRef.current?.contains(element) === true
    },

    getElement: () => containerRef.current
  }))

  let $colour: string | undefined = undefined
  if (colour === 'alt') {
    $colour = Colors.orange[5]
  }

  return (
    <InsetSection
      className="InsetSection"
      ref={containerRef}
      $colour={$colour}
      {...props}
    >
      {children({
        firstInputRef
      })}
    </InsetSection>
  )
}

function StyledSectionInner(
  { children, colour = 'primary', ...props }: SectionProps,
  ref: React.ForwardedRef<ISectionRefMethods>
) {
  ref = useUnstableRef(ref)

  const registerSection = useContext(Context)
  useEffect(() => {
    return registerSection(ref)
  }, [ref, registerSection])

  const containerRef = useRef<HTMLDivElement>(null)
  const firstInputRef = useRef<HTMLElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      firstInputRef.current?.focus({ preventScroll: true })

      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    },

    contains: element => {
      return containerRef.current?.contains(element) === true
    },

    getElement: () => containerRef.current
  }))

  let $colour: string | undefined = undefined
  if (colour === 'alt') {
    $colour = Colors.orange[5]
  }

  return (
    <StyledInsetSection
      className="StyledInsetSection"
      ref={containerRef}
      $colour={$colour}
      {...props}
    >
      {children({
        firstInputRef
      })}
    </StyledInsetSection>
  )
}

function UnstyledSectionInner(
  { children, colour = 'primary', ...props }: SectionProps,
  ref: React.ForwardedRef<ISectionRefMethods>
) {
  ref = useUnstableRef(ref)

  const registerSection = useContext(Context)
  useEffect(() => {
    return registerSection(ref)
  }, [ref, registerSection])

  const containerRef = useRef<HTMLDivElement>(null)
  const firstInputRef = useRef<HTMLElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      firstInputRef.current?.focus({ preventScroll: true })

      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    },

    contains: element => {
      return containerRef.current?.contains(element) === true
    },

    getElement: () => containerRef.current
  }))

  return (
    <div ref={containerRef}>
      {children({
        firstInputRef
      })}
    </div>
  )
}

export const Section = forwardRef(SectionInner)
export const StyledSection = forwardRef(StyledSectionInner)
export const UnstyledSection = forwardRef(UnstyledSectionInner)

function useUnstableRef<T>(ref: React.ForwardedRef<T>): React.ForwardedRef<T> {
  const thisRef = useRef<T>() as React.ForwardedRef<T>
  return ref ?? thisRef
}
